// src/pages/data-center/components/Detail.tsx

import {
  useAddDataToTable,
  useAddNewColumnInTable,
  useDeleteMultipleRowsData,
  useGetTableById,
  useGetTableDataInfinite,
  useUpdateRowData,
} from "@/queries/data-center/data-center.api";
import { useToast } from "@elaniintc/goes-design-system";
import React, { useEffect, useMemo, useState } from "react";
import Table from "./refactor/Table";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useDataCenterStore } from "@/stores/data-center/useCenterStore";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { useNavigate } from "react-router";
import { useDebounceValue } from "usehooks-ts";
import { savedChangesToast } from "./detail.helpers";
import { DetailModals } from "./detail-modals";
import { DetailHeader } from "./detail-header";
import { DetailEmptyState } from "./detail-empty-state";
import { DetailFooter } from "./detail-footer";
import type {
  DetailModalState,
  DetailProps,
  SelectedColumn,
  ValueType,
} from "./detail.types";

export const Detalle: React.FC<DetailProps> = ({ tableId }) => {
  const { hasPermission } = useAuthorization();
  const canCreateRegistry = hasPermission(
    Permissions["data-center"]["create-registry"],
  );
  const canUpdateTable = hasPermission(Permissions["data-center"]["update"]);
  const canUpdateRegistry = hasPermission(
    Permissions["data-center"]["update-registry"],
  );
  const canDeleteRegistry = hasPermission(
    Permissions["data-center"]["delete-registry"],
  );

  const { data, isLoading, refetch } = useGetTableById(tableId);

  const [search, setSearch] = useState("");
  const [diferredSearch] = useDebounceValue(search, 500);

  const [sortState, setSortState] = useState<{
    columnId: string;
    direction: "asc" | "desc" | "off";
  } | null>(null);

  const {
    data: tableData,
    refetch: refetchTableData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetTableDataInfinite({
    name: data?.data?.name || "",
    search: diferredSearch,
    ...(sortState
      ? {
          order_by: `${
            sortState.direction === "asc" ? "" : "!"
          }${sortState.columnId}`,
        }
      : {}),
  });

  const allData = tableData?.pages.flatMap((p) => p?.data?.data || []);

  const { mutateAsync: addColumns, isPending: isLoadingSave } =
    useAddNewColumnInTable();
  const { mutateAsync: addData, isPending: isLoadingAddData } =
    useAddDataToTable();
  const { mutateAsync: updateRowData, isPending: isLoadingUpdateRowData } =
    useUpdateRowData();
  const {
    mutateAsync: deleteMultipleRowsData,
    isPending: isLoadingDeleteRowData,
  } = useDeleteMultipleRowsData();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const { columns } = useDataCenterStore();

  const [selectedRows, setSelectedRows] = useState<
    {
      id: string;
      rowId: string;
    }[]
  >([]);

  const [modalState, setModalState] = useState<DetailModalState>("idle");

  const methods = useForm<{
    rows: {
      id: string;
      created_at: string;
      updated_at: string;
      [key: string]: string;
    }[];
  }>({
    defaultValues: {
      rows: tableData?.pages.flatMap((p) => p?.data?.data || []),
    },
  });
  const { fields, replace, append } = useFieldArray({
    control: methods.control,
    name: "rows",
    keyName: "row-key",
  });

  useEffect(() => {
    if (tableData?.pages) {
      setSelectedRows([]);
      setAreAllRowsSelected(false);
      replace(tableData.pages.flatMap((p) => p?.data?.data || []));
    }
  }, [tableData, replace]);

  const appendNewRow = () => {
    append({
      id: "",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      ...Object.fromEntries(
        data?.data?.columns.map((col: { name: string }) => {
          return { [col.name]: "" };
        }) || [],
      ),
    });
  };

  const onAddNewData = async (columnId: string, value: ValueType) => {
    try {
      await addData({
        table_name: data?.data?.name || "",
        data: {
          [columnId]: value,
        },
      });
      refetchTableData();
      showToast(savedChangesToast);
    } catch (e) {
      const error = e as Error;
      showToast({
        title: "Error al agregar registro",
        description: error.message,
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  };

  const onEditData = async (
    columnId: string,
    value: string | boolean | number,
    registerId: string,
  ) => {
    try {
      await updateRowData({
        id: registerId,
        table_name: data?.data?.name || "",
        data: {
          [columnId]: value,
        },
      });
      refetchTableData();
      showToast(savedChangesToast);
    } catch (e) {
      const error = e as Error;
      showToast({
        title: "Error al editar registro",
        description: error.message,
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  };

  const onDeleteRowsData = async () => {
    try {
      await deleteMultipleRowsData({
        table_name: data?.data?.name || "",
        ids: selectedRows.filter((row) => row.id).map((row) => row.id),
      });
      refetchTableData();
      setSelectedRows([]);
      setAreAllRowsSelected(false);
      setModalState("idle");
      showToast({
        title: "Registros eliminados",
        description: "Los registros han sido eliminados exitosamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    } catch (e) {
      const error = e as Error;
      console.error("Error deleting rows:", error);
      showToast({
        title: "Error al eliminar registros",
        description:
          "No se pudieron eliminar los registros seleccionados. Si ya no existen, alguien más los eliminó.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
      setModalState("idle");
      refetchTableData();
    }
  };

  const onAddData = async (
    columnId: string,
    value: string | boolean | number,
    registerId: string,
  ) => {
    if (!registerId) {
      onAddNewData(columnId, value);
      return;
    }
    onEditData(columnId, value, registerId);
  };

  const [areAllRowsSelected, setAreAllRowsSelected] = useState(false);
  const onSelectAllRows = () => {
    setSelectedRows(
      areAllRowsSelected
        ? []
        : filteredRows.map((row) => ({ id: row.id, rowId: row["row-key"] })),
    );
    setAreAllRowsSelected(!areAllRowsSelected);
  };

  const onSelectRow = (
    val: { id: string; rowId: string },
    isSelected: boolean,
  ) => {
    if (!isSelected) {
      setAreAllRowsSelected(false);
    }

    if (isSelected) {
      setSelectedRows((prev) => [...prev, val]);
    } else {
      setSelectedRows((prev) =>
        prev.filter((row) => row.id !== val.id && row.rowId !== val.rowId),
      );
    }
  };

  const onSubmit = async () => {
    if (columns.length > 0) {
      await addColumns({
        id: tableId,
        name: data?.data?.name || "",
        description: data?.data?.description || "",
        columns: [
          ...(data?.data?.columns || []).map(
            (c: {
              id: string;
              name: string;
              type: string;
              display_name: string;
            }) => ({
              id: c.id,
              name: c.name,
              type: c.type,
              display_name: c.display_name || "",
            }),
          ),
          ...columns.map((col) => ({
            name: col.name,
            type: col.type,
            display_name: col.display_name || "",
          })),
        ],
      });
    }
  };

  const hasUnsavedRows = fields.some((row) => {
    return row.id === "";
  });

  const filteredRows = useMemo(() => {
    return fields.map((row, originalIndex) => ({ ...row, originalIndex }));
  }, [fields]);

  const [selectedColumn, setSelectedColumn] = useState<SelectedColumn | null>(
    null,
  );

  const onEditColumnAttempt = (columnId: string) => {
    const column = data?.data?.columns.find(
      (col: { id: string }) => col.id === columnId,
    );
    if (!column) return;
    setModalState("edit-column");
    setSelectedColumn({
      id: column.id,
      name: column.name,
      data_type: column.type,
      display_name: column.display_name,
    });
  };

  return (
    <>
      <DetailModals
        modalState={modalState}
        setModalState={setModalState}
        onSubmit={onSubmit}
        isLoadingSave={isLoadingSave}
        data={data}
        tableId={tableId}
        refetch={refetch}
        refetchTableData={refetchTableData}
        showToast={showToast}
        selectedColumn={selectedColumn}
        setSelectedColumn={setSelectedColumn}
        isLoadingDeleteRowData={isLoadingDeleteRowData}
        onDeleteRowsData={onDeleteRowsData}
        navigate={navigate}
      />
      <div className="border border-neutral shadow-custom rounded-xl mt-8 flex flex-col h-full overflow-hidden">
        <DetailHeader
          canUpdateTable={canUpdateTable}
          isLoading={isLoading}
          title={
            data?.data?.table_display_name ||
            data?.data?.name ||
            "Nombre de la tabla"
          }
          description={data?.data?.description || ""}
          search={search}
          onSearchChange={setSearch}
          canCreateRegistry={canCreateRegistry}
          onNewRow={() => {
            appendNewRow();
          }}
          isNewRowDisabled={
            hasUnsavedRows ||
            isLoadingDeleteRowData ||
            isLoadingAddData ||
            isLoadingUpdateRowData ||
            data?.data?.columns.length === 0
          }
          onEditTable={() => {
            setModalState("edit-table");
          }}
        />

        <div className="overflow-y-auto flex-1 h-full max-h-full">
          {data?.data?.columns?.length === 0 && (
            <DetailEmptyState
              canUpdateTable={canUpdateTable}
              onAddColumn={() => setModalState("new-column")}
            />
          )}
          {data?.data?.columns?.length > 0 && !isLoading && (
            <FormProvider {...methods}>
              <Table
                columns={data?.data?.columns || []}
                canEditRegistry={canUpdateRegistry}
                canEditTable={canUpdateTable}
                canCreateRegistry={canCreateRegistry}
                sortState={sortState}
                onSort={(columnId, direction) => {
                  if (direction === "off") {
                    setSortState(null);
                    return;
                  }
                  setSortState({
                    columnId,
                    direction,
                  });
                }}
                rows={filteredRows || []}
                onEditColumn={onEditColumnAttempt}
                selectedRows={selectedRows}
                onNewColumn={() => {
                  setModalState("new-column");
                }}
                shouldAllowNewColumn={
                  allData?.length === 0 &&
                  !isLoadingSave &&
                  !isLoadingAddData &&
                  !isLoadingUpdateRowData &&
                  !isLoadingDeleteRowData
                }
                onAddData={onAddData}
                areAllRowsSelected={areAllRowsSelected}
                onSelectAllRows={onSelectAllRows}
                onSelectRow={onSelectRow}
                hasMoreData={hasNextPage}
                isLoadingMoreData={isFetchingNextPage}
                onLoadMore={fetchNextPage}
              />
            </FormProvider>
          )}
        </div>
        <DetailFooter
          rowCount={fields.length}
          selectedCount={selectedRows.length}
          canDeleteRegistry={canDeleteRegistry}
          isDeleteDisabled={
            isLoadingDeleteRowData || isLoadingAddData || isLoadingUpdateRowData
          }
          onDelete={() => {
            setModalState("delete");
          }}
        />
      </div>
    </>
  );
};

export default Detalle;
